import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTracker } from '../../operations/tracking_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { userIsCreator } from '../../services/users';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCreateMealMutation,
  useLazyGetFullMealQuery,
  useUpdateMealMutation,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  updateMembershipWithAppleIAP,
  updateMembershipWithGooglePlayPurchase,
} from '../../operations/user_profile_operations';
import { newMealFromImportedRecipe } from '../../services/meals';
import { newMealAddedToRecipesBoard } from '../../operations/recipes_operations';
import { parseRecipeByUrl } from '../../operations/meal_operations';

const CommonDOMMessageListener = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const tracker = useTracker();

  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery(undefined, {
      skip: !userProfile || isSmorgStudio,
    });

  const currentSpaceID = currentSpaceMembership?.spaceID;

  const customerFoodDataID = customerFoodDataIDOfSpace(currentSpaceID);

  const [createMeal] = useCreateMealMutation();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [updateMeal] = useUpdateMealMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const { data: ownedRecipesBoards, isLoading: isOwnedRecipesBoardsLoading } =
    useGetOwnedRecipesBoardsQuery();

  /**
   * Handles message from Smorg Companion native wrapper
   */
  const handleDOMMessage = useCallback(
    async (message) => {
      if (typeof message !== 'object') {
        // Not interested
        return;
      }

      if (message.source === 'react-devtools-bridge') {
        // Do not interfere with DevTools
        return;
      }
      // console.log(`DOM message: ${JSON.stringify(message.data)}`);

      let messageDataObj = message.data;
      if (typeof message.data === 'string') {
        try {
          messageDataObj = JSON.parse(message.data);
        } catch (e) {
          // Not a valid DOM message; could be e.g. a webpack hot reload command
          // console.warn(e);
          return;
        }
      }

      if (messageDataObj.type === 'add-url') {
        // Add recipe URL from Smorg Companion share sheet extension
        const handleAddUrl = async (url) => {
          const firstRecipesBoard = (ownedRecipesBoards || [])[0];
          if (
            isUserProfileLoading ||
            spaceMembershipLoading ||
            isOwnedRecipesBoardsLoading
          ) {
            console.log('App is still loading, waiting for it to finish');
            window.setTimeout(() => handleAddUrl(url), 2000);
            return false;
          }
          if (!firstRecipesBoard) {
            console.warn('No recipes board found to add the meal into');
            return false;
          }
          navigate(`/recipes/${firstRecipesBoard.id}`);
          const addRecipeResult = await parseRecipeByUrl(url);
          if (addRecipeResult.error) {
            console.warn(
              'Error while parsing the recipe',
              addRecipeResult.error,
            );
            return false;
          }
          const newMeal = newMealFromImportedRecipe(addRecipeResult.recipe);
          const firstMenuInFirstRecipesBoard = firstRecipesBoard.menus?.[0];
          await newMealAddedToRecipesBoard(
            firstRecipesBoard,
            firstMenuInFirstRecipesBoard.id,
            newMeal,
            null,
            customerFoodDataID,
            tracker,
            userProfile,
            currentSpaceMembership,
            null,
            createMeal,
            updateSmorgBoard,
            updateMeal,
            lazyGetMeal,
            () => {},
          );
          return true;
        };
        const { url } = messageDataObj;
        handleAddUrl(url);
      } else if (messageDataObj.type === 'updateMembershipWithAppleIAP') {
        const { purchase } = messageDataObj;
        await updateMembershipWithAppleIAP(
          purchase,
          currentSpaceMembership,
          dispatch,
        );
      } else if (
        messageDataObj.type === 'updateMembershipWithGooglePlayPurchase'
      ) {
        const { purchase } = messageDataObj;
        updateMembershipWithGooglePlayPurchase(
          purchase,
          currentSpaceMembership,
          dispatch,
        );
      }

      /* else {
        console.warn('Could not handle message');
      } */
    },
    [
      createMeal,
      currentSpaceMembership,
      customerFoodDataID,
      dispatch,
      isOwnedRecipesBoardsLoading,
      isUserProfileLoading,
      lazyGetMeal,
      navigate,
      ownedRecipesBoards,
      spaceMembershipLoading,
      tracker,
      updateMeal,
      updateSmorgBoard,
      userProfile,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleDOMMessage);
    return () => {
      window.removeEventListener('message', handleDOMMessage);
    };
  }, [handleDOMMessage]);

  return '';
};

export default CommonDOMMessageListener;
